import { useCallback, useEffect, useMemo, useState } from "react";
import {
  X,
  Plus,
  Pencil,
  Trash2,
  Check,
  Loader2,
  AlertTriangle,
  CalendarDays,
  Repeat,
} from "lucide-react";
import { api } from "@/lib/api";
import { getLocalDateString } from "@/lib/date";

const DAY_SHORT = ["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"];
const DAY_FULL = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function DaySelect({ value, onChange }) {
  return (
    <select
      value={value === null || value === undefined ? "-" : String(value)}
      onChange={(e) => onChange(e.target.value === "-" ? null : Number(e.target.value))}
      data-testid="routine-day-select"
      className="
        w-full rounded-xl bg-white/[0.04] border border-white/[0.08]
        px-3 py-2 text-[13px] text-[#E5E7EB] outline-none
        focus:border-[rgba(0,229,255,0.45)]
      "
    >
      <option value="-">Sin día específico</option>
      {DAY_FULL.map((d, i) => (
        <option key={i} value={i}>
          {d}
        </option>
      ))}
    </select>
  );
}

function RoutineForm({
  initialName = "",
  initialDay = null,
  initialLogros,
  submitLabel,
  onSubmit,
  onCancel,
  submitting,
  error,
  testIdPrefix,
}) {
  const [name, setName] = useState(initialName);
  const [day, setDay] = useState(initialDay);
  const [logros, setLogros] = useState(() =>
    Array.from({ length: 9 }, (_, i) => initialLogros?.[i]?.text ?? ""),
  );

  const canSubmit = name.trim().length > 0 && !submitting;

  const updateLogro = (idx, text) =>
    setLogros((prev) => prev.map((l, i) => (i === idx ? text.slice(0, 120) : l)));

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-form`}>
      <div>
        <label className="text-[10.5px] uppercase tracking-[0.18em] text-[#9CA3AF]">Nombre</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 60))}
          placeholder="Ej. Lunes, Martes intenso..."
          data-testid={`${testIdPrefix}-name-input`}
          className="
            mt-1 w-full rounded-xl bg-white/[0.04] border border-white/[0.08]
            px-3 py-2 text-[14px] text-[#E5E7EB] placeholder:text-[#6B7280] outline-none
            focus:border-[rgba(0,229,255,0.45)]
          "
        />
      </div>
      <div>
        <label className="text-[10.5px] uppercase tracking-[0.18em] text-[#9CA3AF]">
          Día asociado
        </label>
        <div className="mt-1">
          <DaySelect value={day} onChange={setDay} />
        </div>
      </div>

      <div>
        <label className="text-[10.5px] uppercase tracking-[0.18em] text-[#9CA3AF]">
          Los 9 logros
        </label>
        <ul className="mt-1 space-y-1.5" data-testid={`${testIdPrefix}-logros-inputs`}>
          {logros.map((txt, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="font-mono text-[12px] text-[#00E5FF] w-5 shrink-0 text-center">
                {i + 1}
              </span>
              <input
                type="text"
                value={txt}
                onChange={(e) => updateLogro(i, e.target.value)}
                placeholder="(vacío)"
                data-testid={`${testIdPrefix}-logro-${i + 1}`}
                className="
                  flex-1 rounded-lg bg-white/[0.03] border border-white/[0.06]
                  px-3 py-1.5 text-[12.5px] text-[#E5E7EB] placeholder:text-[#6B7280] outline-none
                  focus:border-[rgba(0,229,255,0.35)]
                "
              />
            </li>
          ))}
        </ul>
      </div>

      {error ? (
        <p className="text-[12px] text-[#F87171]" data-testid={`${testIdPrefix}-error`}>
          {error}
        </p>
      ) : null}

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          data-testid={`${testIdPrefix}-cancel`}
          className="
            flex-1 h-10 rounded-full border border-white/[0.08]
            text-[13px] text-[#E5E7EB]/80 hover:bg-white/[0.03]
            disabled:opacity-60
          "
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() =>
            onSubmit({
              name: name.trim(),
              day_of_week: day,
              logros: logros.map((t) => ({ text: t })),
            })
          }
          disabled={!canSubmit}
          data-testid={`${testIdPrefix}-submit`}
          className="
            flex-1 h-10 rounded-full
            bg-[#00E5FF] text-[#0A0E1A] font-semibold text-[13px]
            shadow-[0_0_18px_rgba(0,229,255,0.4)]
            hover:shadow-[0_0_24px_rgba(0,229,255,0.55)]
            disabled:opacity-50 disabled:shadow-none transition-all
            flex items-center justify-center gap-1.5
          "
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" strokeWidth={2.5} />}
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

function ConfirmDelete({ routine, onCancel, onConfirm, busy }) {
  return (
    <div className="space-y-3" data-testid="routine-confirm-delete">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] flex items-center justify-center">
          <AlertTriangle className="h-5 w-5 text-[#F87171]" strokeWidth={1.75} />
        </div>
        <div>
          <p className="text-[14px] font-semibold text-[#E5E7EB]">¿Eliminar rutina?</p>
          <p className="text-[12px] text-[#9CA3AF]">
            &quot;{routine.name}&quot; se borrará. Esta acción no se puede deshacer.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="flex-1 h-10 rounded-full border border-white/[0.08] text-[13px] text-[#E5E7EB]/80 hover:bg-white/[0.03]"
          data-testid="routine-delete-cancel"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="flex-1 h-10 rounded-full bg-[rgba(239,68,68,0.15)] border border-[rgba(239,68,68,0.4)] text-[#F87171] text-[13px] font-semibold hover:bg-[rgba(239,68,68,0.25)] disabled:opacity-60 flex items-center justify-center gap-1.5"
          data-testid="routine-delete-confirm"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Eliminar
        </button>
      </div>
    </div>
  );
}

function RoutineRow({ routine, onApply, onEdit, onDelete, busy }) {
  const dayLabel =
    typeof routine.day_of_week === "number" && routine.day_of_week >= 0 && routine.day_of_week <= 6
      ? DAY_SHORT[routine.day_of_week]
      : null;
  const preview = (routine.logros || [])
    .slice(0, 2)
    .map((l) => l.text?.trim() || "—")
    .filter((t) => t !== "—")
    .join(" · ");

  return (
    <li
      className="
        rounded-2xl bg-white/[0.025] border border-white/[0.06]
        px-3.5 py-3 flex items-center gap-3
      "
      data-testid={`routine-row-${routine.slug}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[13.5px] font-semibold text-[#E5E7EB] truncate">{routine.name}</p>
          {dayLabel ? (
            <span className="text-[9px] font-semibold tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-[rgba(0,229,255,0.1)] border border-[rgba(0,229,255,0.3)] text-[#00E5FF]">
              {dayLabel}
            </span>
          ) : (
            <span className="text-[9px] font-medium tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-white/[0.04] text-[#9CA3AF]">
              SIN DÍA
            </span>
          )}
        </div>
        {preview ? (
          <p className="text-[11.5px] text-[#9CA3AF] truncate mt-0.5">{preview}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onEdit}
        disabled={busy}
        aria-label="Editar rutina"
        data-testid={`routine-edit-${routine.slug}`}
        className="h-8 w-8 rounded-lg flex items-center justify-center text-[#9CA3AF] hover:bg-white/[0.04] hover:text-[#E5E7EB] disabled:opacity-50"
      >
        <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        aria-label="Eliminar rutina"
        data-testid={`routine-delete-${routine.slug}`}
        className="h-8 w-8 rounded-lg flex items-center justify-center text-[#9CA3AF] hover:bg-[rgba(239,68,68,0.1)] hover:text-[#F87171] disabled:opacity-50"
      >
        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
      </button>
      <button
        type="button"
        onClick={onApply}
        disabled={busy}
        data-testid={`routine-apply-${routine.slug}`}
        className="
          h-9 px-3.5 rounded-full
          bg-[#00E5FF] text-[#0A0E1A] text-[12px] font-semibold
          shadow-[0_0_12px_rgba(0,229,255,0.35)]
          hover:shadow-[0_0_18px_rgba(0,229,255,0.5)]
          disabled:opacity-60 transition-shadow
          flex items-center gap-1
        "
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Repeat className="h-3.5 w-3.5" />}
        Aplicar
      </button>
    </li>
  );
}

/**
 * RoutinesSheet — modal/bottom-sheet for browsing, creating, editing, deleting
 * and applying routines. Controlled by the parent (`open` + `onClose`). On any
 * successful apply it calls `onApplied(newLogros)` so the parent can refresh
 * without a round-trip.
 */
export default function RoutinesSheet({
  open,
  onClose,
  currentLogros,
  onApplied,
  onToast,
}) {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [mode, setMode] = useState("list"); // list | create | edit | delete
  const [editTarget, setEditTarget] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const today = useMemo(() => getLocalDateString(), []);

  const fetchRoutines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/rutinas");
      setRoutines(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError("No se pudieron cargar las rutinas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setMode("list");
      setEditTarget(null);
      setFormError(null);
      fetchRoutines();
    }
  }, [open, fetchRoutines]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleApply = async (routine) => {
    setBusyId(routine.routine_id);
    try {
      const res = await api.post(`/rutinas/${routine.routine_id}/apply?date=${encodeURIComponent(today)}`);
      onApplied?.(res.data?.logros || []);
      onToast?.(`Rutina "${routine.name}" aplicada`);
      onClose();
    } catch (e) {
      setError("No se pudo aplicar la rutina.");
    } finally {
      setBusyId(null);
    }
  };

  const handleCreate = async (data) => {
    setFormSubmitting(true);
    setFormError(null);
    try {
      await api.post("/rutinas", data);
      await fetchRoutines();
      setMode("list");
      onToast?.(`Rutina "${data.name}" guardada`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        setFormError("Ya tienes una rutina con ese nombre.");
      } else {
        setFormError("No se pudo guardar la rutina.");
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleUpdate = async (data) => {
    setFormSubmitting(true);
    setFormError(null);
    try {
      await api.put(`/rutinas/${editTarget.routine_id}`, data);
      await fetchRoutines();
      setMode("list");
      setEditTarget(null);
      onToast?.("Rutina actualizada");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        setFormError("Ya tienes una rutina con ese nombre.");
      } else {
        setFormError("No se pudo actualizar.");
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editTarget) return;
    setFormSubmitting(true);
    try {
      await api.delete(`/rutinas/${editTarget.routine_id}`);
      await fetchRoutines();
      setMode("list");
      setEditTarget(null);
      onToast?.("Rutina eliminada");
    } catch (e) {
      setError("No se pudo eliminar.");
    } finally {
      setFormSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-end md:items-center md:justify-center"
      data-testid="routines-sheet-overlay"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />

      {/* Sheet */}
      <div
        className="
          relative w-full md:w-[440px] max-h-[92vh] overflow-hidden
          rounded-t-[28px] md:rounded-[28px]
          bg-[rgba(10,14,26,0.96)] backdrop-blur-xl
          border-t md:border border-[rgba(0,229,255,0.2)]
          shadow-[0_-20px_60px_-10px_rgba(0,0,0,0.6),0_0_60px_-10px_rgba(0,229,255,0.25)]
          flex flex-col
          animate-fade-in-up
        "
        onClick={(e) => e.stopPropagation()}
        data-testid="routines-sheet"
      >
        <header className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/[0.05]">
          <div>
            <h3 className="text-[16px] font-semibold text-[#E5E7EB]">
              Rutinas del Sr. Fernando
            </h3>
            <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">
              {mode === "list"
                ? "Aplique o administre sus presets"
                : mode === "create"
                ? "Nueva rutina"
                : mode === "edit"
                ? `Editar "${editTarget?.name ?? ""}"`
                : "Confirmar"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="routines-sheet-close"
            className="h-9 w-9 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-white/[0.04] hover:text-[#E5E7EB]"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto nx-scroll px-5 py-4">
          {mode === "list" ? (
            <>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-5 w-5 text-[#00E5FF]/70 animate-spin" strokeWidth={1.75} />
                </div>
              ) : error ? (
                <p className="text-[13px] text-[#F87171] text-center py-8" data-testid="routines-error">
                  {error}
                </p>
              ) : routines.length === 0 ? (
                <div className="py-10 text-center" data-testid="routines-empty">
                  <div className="inline-flex h-12 w-12 rounded-full bg-white/[0.02] border border-white/[0.06] items-center justify-center">
                    <CalendarDays className="h-5 w-5 text-[#9CA3AF]" strokeWidth={1.75} />
                  </div>
                  <p className="mt-3 text-[13px] text-[#E5E7EB]">
                    Aún no tienes rutinas
                  </p>
                  <p className="mt-1 text-[11.5px] text-[#9CA3AF] max-w-[260px] mx-auto">
                    Crea la primera guardando tus logros actuales.
                  </p>
                </div>
              ) : (
                <ul className="space-y-2" data-testid="routines-list">
                  {routines.map((r) => (
                    <RoutineRow
                      key={r.routine_id}
                      routine={r}
                      busy={busyId === r.routine_id}
                      onApply={() => handleApply(r)}
                      onEdit={() => {
                        setEditTarget(r);
                        setFormError(null);
                        setMode("edit");
                      }}
                      onDelete={() => {
                        setEditTarget(r);
                        setMode("delete");
                      }}
                    />
                  ))}
                </ul>
              )}

              <div className="mt-5 pt-4 border-t border-white/[0.05]">
                <button
                  type="button"
                  onClick={() => {
                    setFormError(null);
                    setMode("create");
                  }}
                  data-testid="routines-create-button"
                  className="
                    w-full h-12 rounded-full
                    bg-[rgba(0,229,255,0.1)] border border-[rgba(0,229,255,0.4)]
                    text-[#00E5FF] font-semibold text-[13.5px]
                    hover:bg-[rgba(0,229,255,0.18)] hover:border-[rgba(0,229,255,0.6)]
                    shadow-[0_0_14px_rgba(0,229,255,0.2)]
                    flex items-center justify-center gap-2
                    transition-all
                  "
                >
                  <Plus className="h-4 w-4" strokeWidth={2.25} />
                  Guardar rutina actual
                </button>
              </div>
            </>
          ) : mode === "create" ? (
            <RoutineForm
              initialLogros={currentLogros}
              submitLabel="Guardar"
              submitting={formSubmitting}
              error={formError}
              testIdPrefix="routine-create"
              onSubmit={handleCreate}
              onCancel={() => setMode("list")}
            />
          ) : mode === "edit" ? (
            <RoutineForm
              initialName={editTarget?.name || ""}
              initialDay={editTarget?.day_of_week ?? null}
              initialLogros={editTarget?.logros || []}
              submitLabel="Guardar cambios"
              submitting={formSubmitting}
              error={formError}
              testIdPrefix="routine-edit"
              onSubmit={handleUpdate}
              onCancel={() => {
                setMode("list");
                setEditTarget(null);
              }}
            />
          ) : mode === "delete" ? (
            <ConfirmDelete
              routine={editTarget}
              busy={formSubmitting}
              onCancel={() => {
                setMode("list");
                setEditTarget(null);
              }}
              onConfirm={handleDelete}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
